/**
 * Cadência e ordem de execução dos crons do sistema (at-70, frente D/D8).
 *
 * 🔴 **Existe para a tela mostrar o que o texto dela já contava.** A introdução de
 * `/ti/crons` explica em prosa que *"os horários seguem uma ordem deliberada —
 * espelhos de apoio primeiro (6h), depois produtos e cotas, e por fim o rateio
 * (8h); os envios ao SGA rodam a cada 2 horas em minutos escalonados"*. A lista,
 * porém, saía na ordem do array do backend, que **não é cronológica**: o cron do
 * minuto 20 aparecia depois do minuto 25 e depois do desativado, porque foi
 * acrescentado por último (at-35). Agrupar e ordenar é fazer a tela concordar com
 * a própria legenda.
 *
 * ⚠️ **Funções puras sobre a expressão cron** — nada de domínio além do formato.
 * Moram em `utils/` e não em `src/ui` (regra de camada do `13-ui-kit.md`): o kit
 * não sabe o que é um cron.
 *
 * ⚠️ **Nada aqui lança.** A tela é informativa: expressão irreconhecível cai em
 * "Diários" e vai para o fim da ordem, em vez de derrubar a lista. É o mesmo
 * princípio de `parseReferenciaFipe` (at-47) e do status desconhecido em
 * `syncExecucao` (at-67) — relatar o que veio, nunca quebrar.
 */

export type CadenciaCron = 'DIARIO' | 'DUAS_HORAS' | 'DESATIVADO';

/**
 * Título do grupo na tela. O complemento explica o critério, que é a metade da
 * legenda que hoje vive só na introdução.
 */
export const ROTULO_CADENCIA: Record<CadenciaCron, string> = {
  DIARIO: 'Diários — na ordem em que rodam',
  DUAS_HORAS: 'A cada 2 horas — minutos escalonados',
  DESATIVADO: 'Desativados',
};

/** A ordem em que os grupos aparecem na tela. */
export const CADENCIAS_EM_ORDEM: CadenciaCron[] = ['DIARIO', 'DUAS_HORAS', 'DESATIVADO'];

/**
 * `null` é cron desativado — no catálogo do backend, quem não tem agenda não tem
 * expressão (há teste afirmando isso lá).
 *
 * O campo de HORA é o que separa "roda várias vezes por dia" de "roda uma vez por
 * dia": passo (barra), lista (vírgula), intervalo (hífen) ou coringa. Um coringa
 * sozinho na hora também é de várias vezes ao dia — hoje não existe no catálogo,
 * mas classificá-lo como diário seria mentira se surgir.
 *
 * ⚠️ Os exemplos não aparecem escritos aqui de propósito: a sequência
 * `asterisco + barra` fecha o comentário de bloco e quebra o arquivo.
 */
export function cadenciaDoCron(expressao: string | null | undefined): CadenciaCron {
  if (!expressao) return 'DESATIVADO';

  const campos = expressao.trim().split(/\s+/);
  if (campos.length < 2) return 'DIARIO';

  const hora = campos[1];
  const rodaVariasVezesAoDia = hora.includes('/') || hora.includes(',') || hora.includes('-') || hora === '*';

  return rodaVariasVezesAoDia ? 'DUAS_HORAS' : 'DIARIO';
}

/**
 * Minutos desde a meia-noite do **primeiro disparo do dia** — é por este número
 * que os cards são ordenados dentro do grupo.
 *
 * Para os de 2 em 2 horas a hora-base é 0, então o que ordena é o minuto: 0, 5,
 * 10, 15, 20 e 25 — a sequência escalonada que a introdução descreve.
 *
 * ⚠️ Expressão sem número legível vai para o **fim** (`Number.MAX_SAFE_INTEGER`),
 * nunca para o começo: um card órfão no topo da lista seria lido como "é o
 * primeiro a rodar".
 */
export function ordemDeExecucao(expressao: string | null | undefined): number {
  if (!expressao) return Number.MAX_SAFE_INTEGER;

  const campos = expressao.trim().split(/\s+/);
  if (campos.length < 2) return Number.MAX_SAFE_INTEGER;

  const minuto = primeiroNumero(campos[0]);
  const hora = primeiroNumero(campos[1]);

  if (minuto === null && hora === null) return Number.MAX_SAFE_INTEGER;

  return (hora ?? 0) * 60 + (minuto ?? 0);
}

/**
 * O primeiro valor numérico de um campo cron. Coringa e passo (barra) não fixam
 * horário, e por isso valem 0 — o primeiro disparo do dia acontece na hora zero.
 */
function primeiroNumero(campo: string): number | null {
  if (campo === '*') return 0;
  if (campo.startsWith('*/')) return 0;

  const achado = campo.match(/\d+/);
  return achado ? Number(achado[0]) : null;
}
